import React, { useState, useRef } from 'react';
import { XMarkIcon, ArrowPathIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import { whiteLabelStore, DEFAULT_CONFIG } from '../../utils/whiteLabel';
import { ServiceFactory } from '../../services/factory/ServiceFactory';

const WhiteLabelSettings: React.FC = () => {
  const { config, updateConfig, resetConfig } = useWhiteLabel();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    clientName: config.clientName,
    primaryColor: config.primaryColor,
    secondaryColor: config.secondaryColor,
    logoUrl: config.logoUrl,
  });

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, SVG)');
      return;
    }

    try {
      const base64 = await whiteLabelStore.fileToBase64(file);
      setFormData({ ...formData, logoUrl: base64 });
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Validate inputs
      if (!formData.clientName.trim()) {
        setError('Client name is required');
        return;
      }

      if (!/^#[0-9A-Fa-f]{6}$/.test(formData.primaryColor)) {
        setError('Primary color must be a valid hex color (#RRGGBB)');
        return;
      }

      if (!/^#[0-9A-Fa-f]{6}$/.test(formData.secondaryColor)) {
        setError('Secondary color must be a valid hex color (#RRGGBB)');
        return;
      }

      const apiService = ServiceFactory.createApiService();
      const updatedConfig = await apiService.saveWhiteLabelConfig({
        ...config,
        clientName: formData.clientName,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        logoUrl: formData.logoUrl,
      });

      updateConfig(updatedConfig);
      setSuccess('White-label settings saved successfully!');
      setTimeout(() => {
        setSuccess(null);
        setIsOpen(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default Kyndly branding? This will clear all customizations.')) {
      return;
    }

    try {
      const apiService = ServiceFactory.createApiService();
      await apiService.resetWhiteLabelConfig();
      resetConfig();
      setFormData({
        clientName: DEFAULT_CONFIG.clientName,
        primaryColor: DEFAULT_CONFIG.primaryColor,
        secondaryColor: DEFAULT_CONFIG.secondaryColor,
        logoUrl: DEFAULT_CONFIG.logoUrl,
      });
      setSuccess('Reset to default branding');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset');
    }
  };

  const removeLogo = () => {
    setFormData({ ...formData, logoUrl: '' });
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center text-sm px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
      >
        <PhotoIcon className="h-5 w-5 mr-2" />
        White Label Settings
      </button>

      {isOpen && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-start justify-center p-4 bg-night-900/50 pt-8 pb-8">
          <div className="relative bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-2xl w-full mx-auto my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-night-700">
              <h3 className="text-xl font-semibold text-night dark:text-white">White Label Settings</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Client Name */}
                <div>
                  <label className="block text-sm font-medium text-night dark:text-white mb-2">
                    Client Name
                  </label>
                  <input
                    type="text"
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-night-700 text-night dark:text-white"
                    placeholder="Enter client name"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This will replace "Kyndly" throughout the application
                  </p>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-night dark:text-white mb-2">
                    Logo
                  </label>

                  {formData.logoUrl ? (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-gray-300 dark:border-night-600 rounded-lg p-4 bg-gray-50 dark:bg-night-700">
                        <div className="flex items-center justify-between">
                          <img
                            src={formData.logoUrl}
                            alt="Logo preview"
                            className="h-16 object-contain"
                          />
                          <button
                            onClick={removeLogo}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-300 dark:border-night-600 rounded-lg p-8 text-center cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors bg-gray-50 dark:bg-night-700"
                    >
                      <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        Click to upload logo
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PNG, JPG, SVG up to 500KB
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Recommended: 400x100px (wide) or 200x200px (square)
                      </p>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-2">
                      Primary Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.primaryColor}
                        onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                        className="h-10 w-16 rounded border border-gray-300 dark:border-night-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primaryColor}
                        onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-night-700 text-night dark:text-white font-mono text-sm"
                        placeholder="#6BC5A7"
                      />
                    </div>
                    <div
                      className="mt-2 h-8 rounded"
                      style={{ backgroundColor: formData.primaryColor }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-night dark:text-white mb-2">
                      Secondary Color
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.secondaryColor}
                        onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                        className="h-10 w-16 rounded border border-gray-300 dark:border-night-600 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondaryColor}
                        onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-night-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-night-700 text-night dark:text-white font-mono text-sm"
                        placeholder="#0E2C3A"
                      />
                    </div>
                    <div
                      className="mt-2 h-8 rounded"
                      style={{ backgroundColor: formData.secondaryColor }}
                    />
                  </div>
                </div>

                {/* Preview Section */}
                <div className="border-t border-gray-200 dark:border-night-700 pt-6">
                  <h4 className="text-sm font-medium text-night dark:text-white mb-3">Preview</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      {formData.logoUrl && (
                        <img src={formData.logoUrl} alt="Logo" className="h-8 object-contain" />
                      )}
                      <span className="text-lg font-semibold text-night dark:text-white">
                        {formData.clientName}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <div
                        className="flex-1 h-12 rounded flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: formData.primaryColor }}
                      >
                        Primary
                      </div>
                      <div
                        className="flex-1 h-12 rounded flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: formData.secondaryColor }}
                      >
                        Secondary
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Success/Error Messages */}
              {success && (
                <div className="mt-4 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-md">
                  <p>{success}</p>
                </div>
              )}

              {error && (
                <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md">
                  <p>{error}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-6 flex justify-between">
                <button
                  onClick={handleReset}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Reset to Default
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-night-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 border border-transparent rounded-md shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhiteLabelSettings;
